/**
 * Sentry initialization for RentNova.
 *
 * - DSN comes from VITE_SENTRY_DSN. If unset, Sentry stays disabled and
 *   the app behaves as if Sentry isn't installed at all (silent no-op).
 * - Skipped automatically in dev mode so local errors don't pollute the
 *   production project.
 * - tracesSampleRate kept low (0.1) so we don't blow through quota.
 * - PII filter strips known sensitive fields from event payloads —
 *   tenant emails, payment metadata, Supabase JWTs in URLs, etc.
 *
 * If Sentry isn't capturing what you expect, check:
 *   1. VITE_SENTRY_DSN is set in Vercel for the environment you're running
 *   2. You're not on localhost (intentional skip — see below)
 *   3. The Sentry dashboard's "Inbound Filters" aren't dropping events
 */
import * as Sentry from '@sentry/react';
import { getConsent, onConsentChange } from '../components/CookieBanner';

const DSN          = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV          = import.meta.env.MODE;            // 'development' | 'production'
const IS_PROD      = import.meta.env.PROD === true;
const APP_VERSION  = import.meta.env.VITE_APP_VERSION as string | undefined;

let sentryStarted = false;

/** Heuristic: redact obvious PII from arbitrary strings (emails, JWTs). */
function scrubString(s: string): string {
    if (!s) return s;
    // emails
    s = s.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]');
    // Supabase / Stripe-style bearer tokens in URLs (long base64-ish strings)
    s = s.replace(/eyJ[A-Za-z0-9_\-]{20,}/g, '[token]');
    return s;
}

/** Walk a payload tree and scrub any string values in-place. */
function scrubObject(obj: unknown): void {
    if (!obj || typeof obj !== 'object') return;
    for (const key of Object.keys(obj as Record<string, unknown>)) {
        const rec = obj as Record<string, unknown>;
        const v = rec[key];
        if (typeof v === 'string') rec[key] = scrubString(v);
        else if (typeof v === 'object') scrubObject(v);
    }
}

function actuallyStartSentry(): void {
    if (sentryStarted) return;
    if (!DSN) return;
    sentryStarted = true;

    Sentry.init({
        dsn: DSN,
        environment: ENV,
        release: APP_VERSION,
        // Performance monitoring at 10% sample rate — adjust upward when traffic is low.
        tracesSampleRate: 0.1,
        // Don't send default PII (IP address, cookies). We add scrubbing on top below.
        sendDefaultPii: false,
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
        // Drop events whose URL contains sensitive query params (signing tokens, etc.)
        beforeSend(event) {
            try {
                if (event.request?.url) {
                    event.request.url = scrubString(event.request.url);
                }
                scrubObject(event.extra);
                scrubObject(event.contexts);
                scrubObject(event.tags);
                // Strip user email if it slipped through
                if (event.user?.email) event.user.email = '[email]';
            } catch {
                // Better to send a slightly leaky event than to drop it entirely.
            }
            return event;
        },
        // Common noise we don't want to chase
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            'Non-Error promise rejection captured',
        ],
    });
}

/**
 * Public entry point. Respects the user's cookie-consent choice — only starts
 * Sentry once `error_monitoring` is true. If consent hasn't been given yet,
 * we install a listener and start Sentry the moment it is.
 *
 * Why this shape: under GDPR/ePrivacy, loading Sentry before consent counts
 * as setting a tracking technology without a legal basis. Gating here keeps
 * the cookie banner's "Reject all" actually meaningful.
 */
export function initSentry(): void {
    if (!DSN) {
        if (IS_PROD) {
            // eslint-disable-next-line no-console
            console.warn('[sentry] VITE_SENTRY_DSN not set — error monitoring disabled.');
        }
        return;
    }
    if (!IS_PROD) return; // local dev never reports

    const consent = getConsent();
    if (consent?.error_monitoring) {
        actuallyStartSentry();
        return;
    }

    // Wait for the user to accept later (or change their mind in Settings).
    onConsentChange((prefs) => {
        if (prefs.error_monitoring) actuallyStartSentry();
    });
}

/** Re-export the React error boundary so callers don't import @sentry/react directly. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
